import { Controller } from "@hotwired/stimulus"

const REDUCED = "(prefers-reduced-motion: reduce)"

/**
 * A community discussion filling up: votes land, a reply arrives, the score
 * moves, and the post climbs the ranking.
 *
 * Score and rank are read from the markup so the resting state is whatever the
 * page renders, and the animation only ever plays forward from there.
 */
export default class extends Controller {
  static targets = ["score", "up", "reply", "rank", "row"]

  static values = { step: { type: Number, default: 620 } }

  connect() {
    if (window.matchMedia(REDUCED).matches) return

    this.timers = []
    this.baseScore = this.hasScoreTarget ? parseInt(this.scoreTarget.textContent, 10) || 0 : 0

    this.observer = new IntersectionObserver(
      ([entry]) => (entry.isIntersecting ? this.play() : this.reset()),
      { threshold: 0, rootMargin: "-20% 0px -20% 0px" }
    )

    this.observer.observe(this.element)
  }

  disconnect() {
    if (this.observer) this.observer.disconnect()
    this.clearTimers()
  }

  play() {
    if (this.playing) return
    this.playing = true

    const step = this.stepValue
    let t = 300

    // Two votes, spaced unevenly — a steady tick reads like a counter, not people.
    ;[1, 3].forEach((increment, i) => {
      this.after(t, () => this.bumpScore(this.baseScore + increment))
      t += step * (i === 0 ? 0.8 : 1)
    })

    this.replyTargets.forEach((reply) => {
      this.after(t, () => reply.classList.add("is-in"))
      t += step * 0.75
    })

    this.after(t, () => {
      this.rowTargets.forEach((row) => row.classList.add("is-risen"))
      if (this.hasRankTarget) this.rankTarget.textContent = "01"
    })
  }

  bumpScore(value) {
    if (!this.hasScoreTarget) return
    this.scoreTarget.textContent = String(value)
    this.scoreTarget.classList.remove("is-bumped")
    // Force a reflow so the same animation can be replayed back-to-back.
    void this.scoreTarget.offsetWidth
    this.scoreTarget.classList.add("is-bumped")
    this.upTargets.forEach((up) => {
      up.classList.remove("is-cast")
      void up.offsetWidth
      up.classList.add("is-cast")
    })
  }

  reset() {
    this.clearTimers()
    this.playing = false
    if (this.hasScoreTarget) {
      this.scoreTarget.textContent = String(this.baseScore)
      this.scoreTarget.classList.remove("is-bumped")
    }
    this.upTargets.forEach((el) => el.classList.remove("is-cast"))
    this.replyTargets.forEach((el) => el.classList.remove("is-in"))
    this.rowTargets.forEach((el) => el.classList.remove("is-risen"))
    if (this.hasRankTarget) this.rankTarget.textContent = "04"
  }

  after(ms, fn) {
    this.timers.push(setTimeout(fn, ms))
  }

  clearTimers() {
    ;(this.timers || []).forEach(clearTimeout)
    this.timers = []
  }
}
